import {
  StartDownloadProgressFunc,
  UpdateDownloadProgressFunc,
  CompleteDownloadProgressFunc
} from '../contexts/DownloadProgressProvider';
import { DOWNLOAD_INTERNAL_ONLY } from '../constants/accessMessages';
import { INTERNAL_NETWORK_ONLY_CODE } from './internalNetworkError';

/**
 * Content-Dispositionヘッダーからファイル名を抽出する
 * @param contentDisposition Content-Dispositionヘッダーの値
 * @returns 抽出されたファイル名（デコード済み）、取得できない場合はnull
 */
const parseFileNameFromContentDisposition = (contentDisposition: string | undefined): string | null => {
  if (!contentDisposition) {
    return null;
  }
  
  // Content-Dispositionヘッダーからファイル名を抽出
  // パターン例: attachment; filename="video.mp4" または attachment; filename*=UTF-8''video.mp4
  const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  if (fileNameMatch && fileNameMatch[1]) {
    return decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
  }
  
  return null;
};

/**
 * ファイル名に拡張子が含まれていない場合に拡張子を追加
 * @param fileName ファイル名
 * @returns 拡張子を含むファイル名
 */
const ensureFileExtension = (fileName: string): string => {
  if (!fileName.includes('.')) {
    return `${fileName}.mp4`;
  }
  return fileName;
};

/**
 * 動画をダウンロードする共通関数
 * 
 * @param videoId ダウンロードする動画のID
 * @param token 認証トークン
 * @param defaultFileName デフォルトのファイル名（Content-Dispositionが取得できない場合に使用）
 * @param onSuccess 成功時のコールバック関数
 * @param onError エラー時のコールバック関数（エラーメッセージを引数として受け取る）
 * @param progressCallbacks 進捗管理用のコールバック関数（オプショナル）
 * @returns Promise<void>
 */
export const downloadVideo = async (
  videoId: number | string,
  token: string,
  defaultFileName?: string,
  onSuccess?: () => void,
  onError?: (error: string) => void,
  progressCallbacks?: {
    startDownloadProgress: StartDownloadProgressFunc;
    updateDownloadProgress: UpdateDownloadProgressFunc;
    completeDownloadProgress: CompleteDownloadProgressFunc;
  }
): Promise<void> => {
  if (!token || !videoId) {
    const errorMessage = 'ダウンロードに失敗しました：認証情報がありません';
    if (onError) {
      onError(errorMessage);
    }
    return;
  }

  // ダウンロード進捗用のIDを生成
  const downloadId = `download-${videoId}-${Date.now()}`;
  let fileName = defaultFileName || 'video';
  const startTime = Date.now();

  try {
    console.log(`[Download] ダウンロード開始: videoId=${videoId}, downloadId=${downloadId}`);
    
    const url = `/api/videos/${videoId}/download`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const elapsedTime = Date.now() - startTime;
      console.error(`[Download] サーバーエラー: downloadId=${downloadId}, status=${response.status}, elapsed=${elapsedTime}ms`);

      let errorMessage = 'ダウンロードに失敗しました';
      try {
        const errorData = await response.json();
        // 403 + INTERNAL_NETWORK_ONLY: 汎用メッセージで潰さず専用文言を使う（UX R1-c / 仕様書 §2.9）
        if (response.status === 403 && errorData.code === INTERNAL_NETWORK_ONLY_CODE) {
          errorMessage = DOWNLOAD_INTERNAL_ONLY;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } catch {
        // JSONでない場合はデフォルトメッセージを使用
      }

      if (onError) {
        onError(errorMessage);
      }
      return;
    }

    // Content-Dispositionヘッダーからファイル名を取得
    const contentDisposition = response.headers.get('content-disposition');
    const extractedFileName = parseFileNameFromContentDisposition(contentDisposition || undefined);
    fileName = extractedFileName || defaultFileName || 'video';
    
    // 拡張子がなければ追加
    fileName = ensureFileExtension(fileName);

    // Content-Lengthを取得
    const contentLength = response.headers.get('content-length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    // 進捗を開始
    if (progressCallbacks && total > 0) {
      progressCallbacks.startDownloadProgress(downloadId, fileName, total);
      console.log(`[Download] 進捗開始: downloadId=${downloadId}, total=${total} bytes`);
    }

    // ReadableStreamを使ってストリーミングダウンロード
    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('ストリームを取得できませんでした');
    }

    const chunks: Uint8Array[] = [];
    let loaded = 0;

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        break;
      }

      if (value) {
        chunks.push(value);
        loaded += value.length;

        // 進捗を更新
        if (progressCallbacks && total > 0) {
          progressCallbacks.updateDownloadProgress(downloadId, loaded, total);
        }
      }
    }

    // チャンクを結合してBlobを作成
    const blob = new Blob(chunks as BlobPart[]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    // ダウンロード完了を通知
    const elapsedTime = Date.now() - startTime;
    const fileSize = blob.size;
    console.log(`[Download] ダウンロード完了: downloadId=${downloadId}, fileName=${fileName}, size=${fileSize} bytes, elapsed=${elapsedTime}ms`);
    
    if (progressCallbacks) {
      progressCallbacks.completeDownloadProgress(downloadId);
    }

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const elapsedTime = Date.now() - startTime;
    console.error(`[Download] ダウンロードエラー: downloadId=${downloadId}, elapsed=${elapsedTime}ms`, error);
    
    let errorMessage = 'ダウンロードに失敗しました';
    
    if (error instanceof Error) {
      if (error.message.includes('fetch')) {
        errorMessage = 'ネットワークエラーが発生しました。接続を確認して再度お試しください。';
      } else {
        errorMessage = error.message;
      }
    }
    
    if (onError) {
      onError(errorMessage);
    }
  }
};

